import React from 'react'
import DOMPurify from 'dompurify'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { getURLFromBlob } from '@SonarCX/utilities'
import { getWidgetMedia } from '@SonarCX/lib/api'
import {
  selectWidgetArticle,
  loadWidgetArticle
} from '@SonarCX/slices/widgetArticles'
import useStyles from './styles'

type ChangeItemProps = {
  type: string
  id: string
  isOpen: boolean
}

const ChangeItem: React.FC<ChangeItemProps> = ({ type, id, isOpen }) => {
  const dispatch = useAppDispatch()

  const { loaded, loading, articles } = useAppSelector(selectWidgetArticle)

  const usableArticle = React.useMemo(() => articles[id] ?? {}, [articles, id])
  const sanitized = React.useMemo(
    () =>
      DOMPurify.sanitize(usableArticle.body, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
      }),
    [usableArticle.body]
  )
  const [imageBlobs, setImageBlobs] = React.useState([])
  const [loadedMedia, setLoadedMedia] = React.useState(false)

  // Fixes images
  const body = React.useMemo(() => {
    const el = document.createElement('html')
    el.innerHTML = sanitized

    // Add correct image source
    const imgs = el.getElementsByTagName('img')
    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i]
      img.src = imageBlobs[i] ?? ''
    }

    return el.innerHTML
  }, [sanitized, imageBlobs])

  const classes = useStyles()

  React.useEffect(() => {
    if (!isOpen || loaded.includes(id) || loading.includes(id)) {
      return
    }

    dispatch(loadWidgetArticle(id))
  }, [isOpen, loaded, loading, id, dispatch])

  // Gets all media
  React.useEffect(() => {
    if (loadedMedia || !usableArticle?.media) {
      return
    }

    const newMedia = {}

    Promise.all(
      usableArticle.media.map(async mediaObj => {
        const fullMedia = await getWidgetMedia(mediaObj.id)

        const blob = new Blob([fullMedia], { type: mediaObj.mime })
        const url = await getURLFromBlob(blob)

        newMedia[mediaObj.id] = url
      })
    ).then(() => {
      const newUrls = []

      usableArticle.media_ids.forEach(id => {
        newUrls.push(newMedia[id])
      })

      setImageBlobs(newUrls)
      setLoadedMedia(true)
    })
  }, [loadedMedia, usableArticle.media, usableArticle.media_ids])

  if (!loaded.includes(id)) {
    return (
      <div className={classes.root}>
        {loading.includes(id) ? <p>Loading...</p> : <p>No {type} found.</p>}
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div
        className={classes.documentContainer}
        dangerouslySetInnerHTML={{
          __html: body
        }}
      />
    </div>
  )
}

export default ChangeItem
